<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 sm:p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              📊 Dashboard de Cotizaciones
            </h1>
            <p class="text-gray-600">
              Panel de control completo para análisis de cotizaciones y ventas
            </p>
          </div>
          
          <!-- Selector de fecha -->
          <div class="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
            <label class="text-sm font-medium text-gray-700">Período:</label>
            <select 
              v-model="selectedYear" 
              @change="loadData" 
              class="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="2024">2024</option>
              <option value="2025">2025</option>
            </select>
            <select 
              v-model="selectedMonth" 
              @change="loadData" 
              class="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option v-for="(month, index) in months" :key="index" :value="index + 1">
                {{ month }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="bg-white rounded-lg shadow p-8 text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p class="text-gray-600">Cargando datos del dashboard...</p>
        </div>
      </div>

      <!-- Dashboard Content -->
      <div v-else class="space-y-6">
        
        <!-- KPIs Summary -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <!-- Total Cotizaciones -->
          <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-blue-100 text-sm font-medium">Total Cotizaciones</p>
                <p class="text-2xl font-bold">{{ formatNumber(dashboardData?.resumen_general?.total_cotizaciones_ano || 450) }}</p>
              </div>
              <div class="text-blue-200">
                <i class="las la-file-alt text-3xl"></i>
              </div>
            </div>
          </div>

          <!-- Ventas del Mes -->
          <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-green-100 text-sm font-medium">Ventas del Mes</p>
                <p class="text-2xl font-bold">{{ formatNumber(dashboardData?.metricas_mes_actual?.ventas_mes || 25) }}</p>
              </div>
              <div class="text-green-200">
                <i class="las la-shopping-cart text-3xl"></i>
              </div>
            </div>
          </div>

          <!-- Monto Total -->
          <div class="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-purple-100 text-sm font-medium">Monto Total</p>
                <p class="text-xl font-bold">${{ formatCurrency(dashboardData?.resumen_general?.monto_total_ventas_ano || 2500000) }}</p>
              </div>
              <div class="text-purple-200">
                <i class="las la-dollar-sign text-3xl"></i>
              </div>
            </div>
          </div>

          <!-- Tasa de Conversión -->
          <div class="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-yellow-100 text-sm font-medium">Conversión</p>
                <p class="text-2xl font-bold">{{ formatPercentage(dashboardData?.resumen_general?.conversion_rate || 65.5) }}%</p>
              </div>
              <div class="text-yellow-200">
                <i class="las la-chart-line text-3xl"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row 1 - Solo las básicas -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Cotizaciones por Mes -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">📈 Cotizaciones por Mes</h3>
            <apexchart 
              type="line" 
              :options="cotizacionesPorMesOptions" 
              :series="cotizacionesPorMesSeries"
              height="300"
            ></apexchart>
          </div>
          
          <!-- Ventas por Mes -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">💰 Ventas por Mes</h3>
            <apexchart 
              type="area" 
              :options="ventasPorMesOptions" 
              :series="ventasPorMesSeries"
              height="300"
            ></apexchart>
          </div>
        </div>

      </div>

      <!-- Error State -->
      <div v-if="error && !loading" class="bg-white rounded-xl shadow-lg p-8 text-center">
        <div class="text-red-500 mb-4">
          <i class="las la-exclamation-triangle text-4xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Error al cargar datos</h3>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button 
          @click="loadData" 
          class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Reintentar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/stores/user'
import tablero from '@/Services/tablero'
import { emitter } from '@/mitt.js'

export default {
  name: 'DashboardCotizaciones',
  data() {
    return {
      selectedYear: 2025,
      selectedMonth: 9,
      months: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      loading: false,
      error: null,
      dashboardData: null,
      
      // Opciones simplificadas para las gráficas
      cotizacionesPorMesOptions: {
        chart: {
          type: 'line',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        colors: ['#3B82F6'],
        xaxis: {
          categories: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
        },
        yaxis: {
          title: { text: 'Cantidad' }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + ' cotizaciones'
            }
          }
        }
      },
      
      ventasPorMesOptions: {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
            stops: [0, 100]
          }
        },
        colors: ['#10B981'],
        xaxis: {
          categories: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
        },
        yaxis: {
          title: { text: 'Monto ($)' },
          labels: {
            formatter: function(val) {
              return '$' + val.toLocaleString()
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return '$' + val.toLocaleString()
            }
          }
        }
      }
    }
  },
  
  computed: {
    // Series simplificadas con datos estáticos seguros
    cotizacionesPorMesSeries() {
      return [{
        name: 'Cotizaciones',
        data: [10, 15, 8, 20, 25, 18, 30, 22, 16, 28, 35, 40]
      }];
    },
    
    ventasPorMesSeries() {
      return [{
        name: 'Ventas',
        data: [50000, 75000, 40000, 100000, 125000, 90000, 150000, 110000, 80000, 140000, 175000, 200000]
      }];
    }
  },
  
  mounted() {
    this.loadData()
  },
  
  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      
      console.log(`🔄 Cargando datos de prueba para año: ${this.selectedYear}, mes: ${this.selectedMonth}`)
      
      try {
        emitter.emit("loading", true)
        
        // Datos de prueba estáticos
        this.dashboardData = {
          resumen_general: {
            total_cotizaciones_ano: 450,
            monto_total_ventas_ano: 2500000,
            conversion_rate: 65.5,
            ticket_promedio: 55556
          },
          metricas_mes_actual: {
            cotizaciones_mes: 38,
            ventas_mes: 25,
            monto_ventas_mes: 1388900,
            crecimiento_vs_mes_anterior: 12.5
          }
        }
        
        console.log('✅ Datos de prueba cargados exitosamente')
      } catch (error) {
        console.error('❌ Error loading dashboard data:', error)
        this.error = 'Error de conexión. Por favor, inténtelo de nuevo.'
      } finally {
        this.loading = false
        emitter.emit("loading", false)
      }
    },
    
    formatNumber(num) {
      return parseInt(num || 0).toLocaleString()
    },
    
    formatCurrency(amount) {
      return parseFloat(amount || 0).toLocaleString('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },
    
    formatPercentage(percent) {
      return parseFloat(percent || 0).toFixed(1)
    }
  }
}
</script>

<style scoped>
/* Additional custom styles if needed */
.las {
  font-family: 'Line Awesome Free', 'Line Awesome', sans-serif;
}
</style>
